import { ObjectCommentObjectType } from "src/api/generated/erp/db/types/enums/objectCommentObjectType";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender";
import { ObjectCommentApi } from "src/api/generated/erp/common/objectComments/objectCommentApi.ts";
import { Box, List, ListItem, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useRef, useState } from "react";
import { VerticalBox } from "src/components/common/box/VerticalBox";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faArrowUp } from "@fortawesome/pro-regular-svg-icons";
import i18n from "i18next";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { formatIsoString } from "src/utils/dayjsUtils.ts";

const COMMENT_SUBMIT_BUTTON_SIZE = 32;
const EXPANDED_COMMENT_INPUT_LENGTH = 80;

export interface ObjectCommentingViewProps {
	objectType: ObjectCommentObjectType;
	objectId: number;
	editable?: boolean;
}

export const ObjectCommentingView = ({ objectType, objectId, editable = true }: ObjectCommentingViewProps) => {
	const { logErrorAndShowOnDialog } = useErrorDialog();
	const inputRef = useRef<HTMLTextAreaElement | null>(null);
	const commentListRef = useRef<HTMLUListElement | null>(null);

	const [newComment, setNewComment] = useState("");
	// Like the Aavo AI composer, long comments get the full width and the send button moves below them.
	const isExpandedInput = newComment.includes("\n") || newComment.length > EXPANDED_COMMENT_INPUT_LENGTH;

	return (
		<AsyncFetchRender
			fetch={() => ObjectCommentApi.getObjectComments({ objectType, objectId })}
			content={(comments, reload) => (
				<VerticalBox sx={{ alignItems: "stretch", flex: 1 }}>
					<List
						ref={commentListRef}
						sx={{
							overflow: "auto",
							flex: 1,
							display: "flex",
							flexDirection: "column-reverse",
						}}
					>
						{comments.map((comment) => (
							<ListItem
								key={comment.objectCommentId}
								sx={{
									display: "block",
									borderBottom: "1px solid",
									borderColor: "divider",
									paddingX: 1.5,
									paddingY: 0.75,
								}}
							>
								<Typography
									variant={"caption"}
									color={"text.secondary"}
									sx={{
										display: "block",
										lineHeight: 1.3,
									}}
								>
									<Box component={"span"} sx={{ fontWeight: "bold" }}>
										{comment.createdByUserName}
									</Box>
									{" · "}
									{formatIsoString(comment.createdAt, "L LT")}
								</Typography>
								<Typography
									variant={"body2"}
									sx={{
										marginTop: 0.25,
										lineHeight: 1.45,
										whiteSpace: "pre-line",
										overflowWrap: "anywhere",
									}}
								>
									{comment.comment}
								</Typography>
							</ListItem>
						))}
					</List>
					{editable && (
						<Box
							onClick={(event) => {
								// Focus the input on padding clicks; the input and send button handle their own clicks.
								if (event.target === event.currentTarget) inputRef.current?.focus();
							}}
							sx={{
								cursor: "text",
								margin: 1,
								display: "flex",
								flexWrap: isExpandedInput ? "wrap" : "nowrap",
								alignItems: "center",
								justifyContent: "flex-end",
								gap: 0.75,
								border: "1px solid",
								borderColor: (theme) => alpha(theme.palette.text.primary, 0.16),
								borderRadius: 2,
								backgroundColor: "background.paper",
								boxShadow: "0 2px 10px rgba(0, 0, 0, 0.07)",
								paddingX: 0.75,
								paddingY: 0.75,
							}}
						>
							<TextField
								inputRef={inputRef}
								value={newComment}
								onChange={(event) => setNewComment(event.currentTarget.value)}
								multiline
								maxRows={5}
								fullWidth
								sx={{
									flex: 1,
									flexBasis: isExpandedInput ? "100%" : 0,
									minWidth: 0,
								}}
								placeholder={i18n.t("new_comment")}
								slotProps={{
									input: {
										sx: {
											typography: "body2",
											lineHeight: 1.45,
											fontSize: 13,
											minHeight: COMMENT_SUBMIT_BUTTON_SIZE,
											paddingY: 0,
											paddingX: 0,
											display: "flex",
											alignItems: "center",
											backgroundColor: "transparent",
											"& fieldset": {
												border: "none",
											},
											"& .MuiInputBase-input": {
												paddingY: 0,
												paddingX: 0,
											},
										},
									},
								}}
								onKeyDown={(event) => {
									if (
										event.key !== "Enter" ||
										event.shiftKey ||
										event.ctrlKey ||
										event.altKey ||
										event.metaKey
									) {
										return;
									}
									event.preventDefault();
									void sendMessage(reload);
								}}
							/>
							<AsyncButton
								icon={faArrowUp}
								tooltip={i18n.t("send")}
								onClick={() => sendMessage(reload)}
								variant={"contained"}
								disabled={newComment.trim() === ""}
								sx={{
									flexShrink: 0,
									minWidth: COMMENT_SUBMIT_BUTTON_SIZE,
									width: COMMENT_SUBMIT_BUTTON_SIZE,
									height: COMMENT_SUBMIT_BUTTON_SIZE,
									padding: 0,
									borderRadius: "50%",
									"& svg": { fontSize: 14 },
								}}
							/>
						</Box>
					)}
				</VerticalBox>
			)}
		/>
	);

	async function sendMessage(reload: () => Promise<unknown>) {
		const content = newComment.trim();
		if (content === "") return;

		try {
			await ObjectCommentApi.addComment({
				objectType: objectType,
				objectId: objectId,
				content: content,
			});
			setNewComment("");
			await reload();
			// The list is column-reversed, so scroll position 0 shows the newest comment.
			commentListRef.current?.scrollTo({ top: 0 });
		} catch (e) {
			logErrorAndShowOnDialog(e);
		}
	}
};
